/**
 * StockTransferDialog — modal that creates an inter-godown stock transfer.
 *
 * Flow:
 *  1. Pick source + destination godowns (must differ).
 *  2. Add product lines via inline search + qty.
 *  3. Submit → atomic write through StockTransferService.
 *
 * Reactive godown/product queries via useObservable.
 */

import { notifySuccess } from '@/store/useToastStore';
import { useErrorPopup } from '@/hooks/useErrorPopup';
import { useEffect, useMemo, useState } from 'react';
import { Q } from '@nozbe/watermelondb';
import { Plus, Trash2, ArrowRight } from 'lucide-react';
import { database } from '@/db';
import { Product, Godown } from '@/db/models/Inventory';
import { useAppStore } from '@/store/useAppStore';
import { useObservable } from '@/hooks/useObservable';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { createStockTransfer, type TransferLine } from '@/inventory/StockTransferService';

interface DraftLine {
  product: Product;
  quantity: string; // editable string; parsed on submit
}

export function StockTransferDialog({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const userId = useAppStore((s) => s.currentUser?.id);

  const godowns = useObservable<Godown[]>(
    () => {
      if (!activeStoreId) return null;
      return database.get<Godown>('godowns')
        .query(Q.where('store_id', activeStoreId), Q.where('is_deleted', false))
        .observe();
    },
    [activeStoreId],
    [],
  );

  const [fromGodownId, setFromGodownId] = useState<string>('');
  const [toGodownId, setToGodownId] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [error, setError] = useState<string | null>(null);
  useErrorPopup(error, () => setError(null));
  const [saving, setSaving] = useState(false);

  // initialize default godowns once they load
  useEffect(() => {
    if (godowns.length === 0) return;
    if (!fromGodownId) {
      const def = godowns.find((g) => g.isDefault) ?? godowns[0];
      setFromGodownId(def.id);
    }
    if (!toGodownId && godowns.length > 1) {
      const first = godowns.find((g) => g.id !== fromGodownId);
      if (first) setToGodownId(first.id);
    }
  }, [godowns, fromGodownId, toGodownId]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim().toLowerCase()), 180);
    return () => clearTimeout(t);
  }, [search]);

  const searchResults = useObservable<Product[]>(
    () => {
      if (!activeStoreId || debounced.length < 2) return null;
      const q = `%${debounced}%`;
      return database.get<Product>('products')
        .query(
          Q.where('store_id', activeStoreId),
          Q.where('is_deleted', false),
          Q.where('track_inventory', true),
          Q.or(Q.where('name', Q.like(q)), Q.where('sku', Q.like(q)), Q.where('barcode', Q.like(q))),
          Q.take(20),
        )
        .observe();
    },
    [activeStoreId, debounced],
    [],
  );

  const visibleResults = useMemo(
    () => searchResults.filter((p) => !lines.some((l) => l.product.id === p.id)),
    [searchResults, lines],
  );

  function addLine(p: Product) {
    setLines((prev) => [...prev, { product: p, quantity: '1' }]);
    setSearch('');
  }

  function removeLine(id: string) {
    setLines((prev) => prev.filter((l) => l.product.id !== id));
  }

  function updateQty(id: string, qty: string) {
    setLines((prev) => prev.map((l) => (l.product.id === id ? { ...l, quantity: qty } : l)));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!fromGodownId || !toGodownId) {
      setError('Pick both source and destination godowns.');
      return;
    }
    if (fromGodownId === toGodownId) {
      setError('Source and destination must differ.');
      return;
    }
    if (lines.length === 0) {
      setError('Add at least one product.');
      return;
    }
    const transferLines: TransferLine[] = [];
    for (const l of lines) {
      const qty = parseFloat(l.quantity);
      if (!Number.isFinite(qty) || qty <= 0) {
        setError(`Invalid quantity for ${l.product.name}.`);
        return;
      }
      transferLines.push({ product: l.product, quantity: qty });
    }
    if (!userId) {
      setError('Not signed in.');
      return;
    }

    setSaving(true);
    const res = await createStockTransfer({
      fromGodownId,
      toGodownId,
      lines: transferLines,
      notes: notes.trim() || null,
      createdById: userId,
    });
    setSaving(false);
    if (res.success) {
      notifySuccess(`Stock transfer saved — ${transferLines.length} item${transferLines.length === 1 ? '' : 's'} moved.`);
      onDone();
    } else {
      setError(res.error ?? 'Failed to create transfer.');
    }
  }

  if (godowns.length < 2) {
    return (
      <div className="px-5 py-6">
        <p className="text-body text-fg-secondary">
          You need at least two godowns to transfer stock. Add a second godown in Settings first.
        </p>
        <div className="mt-4 flex justify-end">
          <Button variant="ghost" onClick={onCancel}>Close</Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col">
      <div className="space-y-4 px-5 py-4">
        {/* Godown selectors */}
        <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-3">
          <label className="block">
            <span className="mb-1 block text-label uppercase text-fg-tertiary">From</span>
            <select
              className="w-full rounded-md border border-stroke-hairline/20 bg-bg-canvas px-3 py-2 text-body text-fg-primary"
              value={fromGodownId}
              onChange={(e) => setFromGodownId(e.target.value)}
            >
              {godowns.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </label>
          <ArrowRight size={18} className="mb-2 text-fg-tertiary" />
          <label className="block">
            <span className="mb-1 block text-label uppercase text-fg-tertiary">To</span>
            <select
              className="w-full rounded-md border border-stroke-hairline/20 bg-bg-canvas px-3 py-2 text-body text-fg-primary"
              value={toGodownId}
              onChange={(e) => setToGodownId(e.target.value)}
            >
              {godowns.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </label>
        </div>

        {/* Product search */}
        <div className="relative">
          <Input
            placeholder="Add product (search by name, SKU, barcode)…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {visibleResults.length > 0 && (
            <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-60 overflow-auto rounded-md border border-stroke-hairline/20 bg-bg-surface shadow-lg">
              {visibleResults.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => addLine(p)}
                  className="flex w-full items-center justify-between px-3 py-2 text-left text-body hover:bg-bg-subtle"
                >
                  <span className="text-fg-primary">{p.name}</span>
                  <span className="text-label text-fg-tertiary">Stock: {p.currentStock} {p.unit}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Lines table */}
        <div className="rounded-md border border-stroke-hairline/10 bg-bg-canvas">
          {lines.length === 0 ? (
            <div className="px-3 py-6 text-center text-body text-fg-tertiary">No items added yet.</div>
          ) : (
            <table className="w-full text-body">
              <thead>
                <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                  <th className="px-3 py-2 text-left">Product</th>
                  <th className="px-3 py-2 text-right w-28">On hand</th>
                  <th className="px-3 py-2 text-right w-32">Quantity</th>
                  <th className="px-3 py-2 w-10" />
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.product.id} className="border-b border-stroke-hairline/5 last:border-0">
                    <td className="px-3 py-2 text-fg-primary">{l.product.name}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-fg-secondary">
                      {l.product.currentStock} {l.product.unit}
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        value={l.quantity}
                        onChange={(e) => updateQty(l.product.id, e.target.value)}
                        inputMode="decimal"
                        className="text-right"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => removeLine(l.product.id)}
                        className="rounded p-1 text-fg-tertiary hover:bg-bg-subtle hover:text-danger-ink"
                        aria-label="Remove line"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <label className="block">
          <span className="mb-1 block text-label uppercase text-fg-tertiary">Notes (optional)</span>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Reason or remarks" />
        </label>

      </div>

      <div className="flex items-center justify-end gap-2 border-t border-stroke-hairline/10 px-5 py-3">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={saving || lines.length === 0}>
          {saving ? 'Saving…' : <><Plus size={14} /> Create transfer</>}
        </Button>
      </div>
    </form>
  );
}
